import { GOALS } from '@/lib/goals';
import { AdminMealStructure, structureFields } from '@/components/MealPreferences';
import { useState, useEffect } from 'react';
import { api, localDate } from '@/lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Sparkles, Eye, CheckCircle2, UserCircle, Dumbbell, UtensilsCrossed, ArrowRight, Loader2, Save } from 'lucide-react';
import EditableWorkoutPlan from './EditableWorkoutPlan';
import NutritionProgramReview from './NutritionProgramReview';

// Normalize AI drafts into a multi-week shape: { ...top, weeks: [{week_number, focus, <listKey>:[...]}] }.
// Handles both the new multi-week response and legacy single-week (workouts/items at top level).
function normalizeWeeks(draft, listKey) {
  if (!draft) return draft;
  if (Array.isArray(draft.weeks) && draft.weeks.length) {
    return { ...draft, weeks: draft.weeks.map((w, i) => ({ week_number: w.week_number || i + 1, focus: w.focus || '', ...w })) };
  }
  const wk = { week_number: 1, focus: draft.focus || '', [listKey]: draft[listKey] || [] };
  return { ...draft, weeks: [wk] };
}

// Poll an AI background job until it completes or fails. Avoids Cloudflare 524 timeouts.
async function pollAIJob(endpoint, payload, { onTick } = {}) {
  const start = await api.post(endpoint, payload);
  const jobId = start.data.job_id;
  if (!jobId) return start.data; // backward-compatible (sync response)
  const startedAt = Date.now();
  const MAX_MS = 5 * 60 * 1000; // 5 min hard ceiling
  while (Date.now() - startedAt < MAX_MS) {
    await new Promise(r => setTimeout(r, 2500));
    if (onTick) onTick(Math.round((Date.now() - startedAt) / 1000));
    const s = await api.get(`/ai/status/${jobId}`);
    if (s.data.status === 'completed') return s.data;
    if (s.data.status === 'failed') throw new Error(s.data.error || 'AI generation failed');
  }
  throw new Error('AI generation timed out. Please try again.');
}

function GeneratingState({ elapsed, color }) {
  return (
    <div className="text-center py-12" data-testid="ai-generating-state">
      <Loader2 className="w-10 h-10 mx-auto animate-spin mb-4" style={{ color }} />
      <div className="font-display font-bold text-lg">Generating personalized program…</div>
      <div className="text-sm text-muted-foreground mt-1">This usually takes 20–60 sec. Hang tight.</div>
      <div className="text-xs font-mono text-muted-foreground mt-3" data-testid="ai-generating-elapsed">{elapsed}s elapsed</div>
    </div>
  );
}

// Only the client's actual goal is used — never a guessed default.
function mapGoal(goalType) {
  return GOALS.includes(goalType) ? goalType : '';
}

function profileFromClient(c) {
  if (!c) return null;
  const ob = c.onboarding_data || {};
  const weight = c.current_weight_kg ?? ob.current_weight_kg ?? c.starting_weight_kg ?? 80;
  const goalWeight = c.goal_weight_kg ?? ob.target_weight_kg ?? c.target_weight_kg ?? weight;
  const gender = c.gender ?? ob.gender ?? 'male';
  return {
    client_id: c.id, name: c.name,
    age: c.age ?? ob.age ?? 35,
    gender: String(gender).toLowerCase().includes('f') ? 'female' : 'male',
    goal: mapGoal(c.goal_type || ob.primary_goal || c.primary_goal),
    fitness_level: c.fitness_level || ob.fitness_level || 'intermediate',
    training_frequency: c.training_frequency || ob.training_frequency || ob.sessions_per_week || 4,
    weight_kg: weight, goal_weight_kg: goalWeight,
    activity_level: c.activity_level || ob.activity_level || 'moderate',
  };
}

export default function AdminAI() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [prefill, setPrefill] = useState(null);
  const [exercises, setExercises] = useState([]);

  useEffect(() => {
    api.get('/clients').then(r => setClients(r.data || [])).catch(() => {});
    api.get('/exercises').then(r => setExercises(r.data || [])).catch(() => {});
  }, []);

  const selectClient = (id) => {
    setClientId(id);
    const c = clients.find(x => x.id === id);
    const p = profileFromClient(c);
    setPrefill(p);
    if (p) toast.success(`Loaded ${p.name}'s profile`);
  };
  const selected = clients.find(c => c.id === clientId);

  return (
    <div className="space-y-6" data-testid="admin-ai">
      <div>
        <div className="label-mini text-[#00C8FF]">AI Studio</div>
        <h1 className="font-display font-black text-4xl mt-1">AI Program Generator</h1>
        <p className="text-muted-foreground text-sm mt-1">Select client → AI drafts → you review &amp; edit → Approve &amp; Assign. AI never assigns automatically.</p>
      </div>

      <div className="regen-card-elevated p-5 blueprint-grid" data-testid="ai-client-profile">
        <div className="flex items-center gap-2 mb-3">
          <UserCircle className="w-5 h-5 text-[var(--c-green-ink)]" />
          <div className="font-display font-bold">Generate from Client Profile</div>
        </div>
        <div>
          <Label>Select a client — their goal, age, weight, fitness level &amp; sessions auto-fill the generators below</Label>
          <Select value={clientId} onValueChange={selectClient}>
            <SelectTrigger className="bg-regen-card border-[var(--c-hair)] mt-1" data-testid="ai-client-select"><SelectValue placeholder="Choose a client…" /></SelectTrigger>
            <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.email}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {selected && prefill && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="ai-profile-chips">
            <Chip label="Goal" value={selected.goal_type || selected.onboarding_data?.primary_goal || prefill.goal} />
            <Chip label="Age" value={prefill.age} />
            <Chip label="Weight" value={`${prefill.weight_kg} kg`} />
            <Chip label="Target" value={`${prefill.goal_weight_kg} kg`} />
            <Chip label="Level" value={prefill.fitness_level} />
            <Chip label="Sessions/wk" value={prefill.training_frequency} />
          </div>
        )}
      </div>

      <Tabs defaultValue="training">
        <TabsList className="bg-regen-card border border-[var(--c-hair)]">
          <TabsTrigger value="training" data-testid="ai-tab-training"><Dumbbell className="w-4 h-4 mr-1.5" /> 1. Training</TabsTrigger>
          <TabsTrigger value="nutrition" data-testid="ai-tab-nutrition"><UtensilsCrossed className="w-4 h-4 mr-1.5" /> 2. Nutrition</TabsTrigger>
        </TabsList>
        <TabsContent value="training" className="mt-4"><TrainingAI prefill={prefill} exercises={exercises} /></TabsContent>
        <TabsContent value="nutrition" className="mt-4"><NutritionAI prefill={prefill} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Chip({ label, value }) {
  return <span className="pill bg-[var(--c-film)] text-xs"><span className="text-muted-foreground">{label}:</span> <span className="font-semibold">{value}</span></span>;
}

const DURATIONS = [1, 2, 4, 8, 12];

function DurationSelect({ value, onChange }) {
  return (
    <div><Label>Program Duration</Label>
      <Select value={String(value)} onValueChange={v => onChange(parseInt(v))}>
        <SelectTrigger className="bg-regen-card border-[var(--c-hair)]" data-testid="ai-duration-select"><SelectValue /></SelectTrigger>
        <SelectContent>{DURATIONS.map(d => <SelectItem key={d} value={String(d)}>{d} week{d > 1 ? 's' : ''}{d === 4 ? ' (recommended)' : ''}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function WeekTabs({ weeks, active, onSelect, showFocus = true }) {
  if (!weeks || weeks.length <= 1) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="ai-week-tabs">
      {weeks.map((w, i) => (
        <button key={i} onClick={() => onSelect(i)} data-testid={`ai-week-tab-${i}`}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition ${i === active ? 'bg-[#00C8FF] text-[#081726]' : 'bg-[var(--c-film)] text-muted-foreground'}`}>
          W{w.week_number || i + 1}{showFocus && w.focus ? ` · ${w.focus}` : ''}
        </button>
      ))}
    </div>
  );
}

function DraftBanner({ name }) {
  return (
    <div className="rounded-xl bg-[var(--c-green-soft)] border border-[var(--c-green-soft)] p-3 mb-4" data-testid="ai-draft-banner">
      <div className="text-sm font-display font-bold text-[var(--c-green-ink)]">Draft generated{name ? ` for ${name}` : ''}</div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
        Review <ArrowRight className="w-3 h-3" /> Edit <ArrowRight className="w-3 h-3" /> Approve &amp; Assign
      </div>
    </div>
  );
}

function AssignControls({ clientId, startDate, setStartDate, planWeeks, reviewed, setReviewed, onAssign, assigning, onSaveTemplate, savingTemplate, color }) {
  const ready = clientId && startDate && reviewed;
  return (
    <div className="border-t border-[var(--c-hair)] mt-4 pt-4 space-y-3" data-testid="ai-assign-controls">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Start Date</Label><Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="ai-start-date" className="bg-regen-card border-[var(--c-hair)] font-mono" /></div>
        <div><Label>Duration</Label><div className="h-10 flex items-center px-3 rounded-md bg-regen-card border border-[var(--c-hair)] font-mono text-sm" data-testid="ai-plan-weeks">{planWeeks} week{planWeeks > 1 ? 's' : ''}</div></div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer text-sm">
        <Checkbox checked={reviewed} onCheckedChange={setReviewed} data-testid="ai-reviewed-check" />
        I've reviewed &amp; edited this draft
      </label>
      {!clientId && <div className="text-xs text-[#FFB800]">Select a client above to assign — or Save as Template to reuse later.</div>}
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={onSaveTemplate} disabled={savingTemplate} data-testid="ai-save-template" className="border-[var(--c-hair)] font-semibold">
          <Save className="w-4 h-4 mr-2" /> {savingTemplate ? 'Saving…' : 'Save as Template'}
        </Button>
        <Button onClick={onAssign} disabled={!ready || assigning} data-testid="ai-approve-assign"
          className="font-semibold disabled:opacity-40" style={{ background: color, color: '#081726' }}>
          <CheckCircle2 className="w-4 h-4 mr-2" /> {assigning ? 'Assigning…' : 'Approve & Assign'}
        </Button>
      </div>
    </div>
  );
}

function TrainingAI({ prefill, exercises }) {
  const [f, setF] = useState({ client_id: null, age: 35, gender: 'male', goal: '', fitness_level: 'intermediate', training_frequency: 4, equipment: 'full gym', injuries: '', duration_weeks: 4 });
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [startDate, setStartDate] = useState(localDate());
  const [reviewed, setReviewed] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [weekIdx, setWeekIdx] = useState(0);

  useEffect(() => {
    if (prefill) setF(prev => ({ ...prev, client_id: prefill.client_id, age: prefill.age, gender: prefill.gender, goal: prefill.goal, fitness_level: prefill.fitness_level, training_frequency: prefill.training_frequency }));
  }, [prefill]);

  const generate = async () => {
    if (!f.goal) return toast.error('Choose a goal: Lose Fat, Build Muscle or Strength & Longevity');
    setLoading(true); setReviewed(false); setElapsed(0); setWeekIdx(0);
    try {
      const data = await pollAIJob('/ai/generate-training', f, { onTick: setElapsed });
      setDraft(normalizeWeeks(data.draft, 'workouts')); toast.success('Training draft generated');
    }
    catch (err) { toast.error(err.response?.data?.detail || err.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const payloadWeeks = () => (draft?.weeks || []).map(w => ({ week_number: w.week_number, focus: w.focus, workouts: w.workouts }));

  const approveAssign = async () => {
    setAssigning(true);
    try {
      const r = await api.post('/ai/approve-assign-training', {
        client_id: f.client_id, start_date: startDate, duration_weeks: draft.weeks.length,
        program_name: draft.program_name || 'AI Training Program', goal: f.goal, weeks: payloadWeeks(),
      });
      toast.success(`Assigned · ${r.data.workouts_created} sessions scheduled`);
      setDraft(null); setReviewed(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Assign failed'); }
    finally { setAssigning(false); }
  };

  const saveTemplate = async () => {
    setSavingTemplate(true);
    try {
      await api.post('/ai/save-template', { kind: 'training', name: draft.program_name || 'AI Training Program',
        goal: f.goal, duration_weeks: draft.weeks.length, weeks: payloadWeeks() });
      toast.success('Saved as template');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSavingTemplate(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="regen-card-elevated p-6 space-y-4">
        <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#00C8FF]" /><div className="font-display font-bold">Training Generator</div>
          {f.client_id && <span className="pill bg-[var(--c-green-soft)] text-[var(--c-green-ink)] text-[10px] ml-auto">From profile</span>}</div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Age</Label><Input type="number" value={f.age} onChange={e => setF({...f, age: parseInt(e.target.value)})} className="bg-regen-card border-[var(--c-hair)]" /></div>
          <div><Label>Gender</Label>
            <Select value={f.gender} onValueChange={v => setF({...f, gender: v})}>
              <SelectTrigger className="bg-regen-card border-[var(--c-hair)]"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div><Label>Main Goal</Label>
          <Select value={f.goal} onValueChange={v => setF({...f, goal: v})}>
            <SelectTrigger className="bg-regen-card border-[var(--c-hair)]" data-testid="ai-train-goal"><SelectValue placeholder="Choose goal" /></SelectTrigger>
            <SelectContent>{GOALS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Fitness Level</Label>
          <Select value={f.fitness_level} onValueChange={v => setF({...f, fitness_level: v})}>
            <SelectTrigger className="bg-regen-card border-[var(--c-hair)]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="beginner">Beginner</SelectItem><SelectItem value="intermediate">Intermediate</SelectItem><SelectItem value="advanced">Advanced</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label>Sessions / Week</Label><Input type="number" value={f.training_frequency} onChange={e => setF({...f, training_frequency: parseInt(e.target.value)})} className="bg-regen-card border-[var(--c-hair)] font-mono" /></div>
        <DurationSelect value={f.duration_weeks} onChange={v => setF({...f, duration_weeks: v})} />
        <div><Label>Equipment</Label><Input value={f.equipment} onChange={e => setF({...f, equipment: e.target.value})} className="bg-regen-card border-[var(--c-hair)]" /></div>
        <div><Label>Injuries</Label><Textarea value={f.injuries} onChange={e => setF({...f, injuries: e.target.value})} className="bg-regen-card border-[var(--c-hair)]" /></div>
        <Button onClick={generate} disabled={loading} data-testid="ai-train-generate" className="w-full bg-[#00C8FF] text-[#081726] font-semibold">
          <Sparkles className="w-4 h-4 mr-2" /> {loading ? 'Generating…' : 'Generate Training Draft'}
        </Button>
      </div>

      <div className="regen-card-elevated p-6">
        <div className="flex items-center gap-2 mb-4"><Eye className="w-5 h-5 text-[var(--c-green-ink)]" /><div className="font-display font-bold">Review &amp; Edit Draft</div></div>
        {loading ? <GeneratingState elapsed={elapsed} color="#00C8FF" /> : !draft ? <div className="text-muted-foreground text-sm py-12 text-center">Generate a program to review here.</div> : (
          <div className="space-y-4">
            <DraftBanner name={prefill?.name} />
            <WeekTabs weeks={draft.weeks} active={weekIdx} onSelect={setWeekIdx} />
            <EditableWorkoutPlan draft={draft} setDraft={setDraft} exercises={exercises} weekIndex={weekIdx} />
            <AssignControls clientId={f.client_id} startDate={startDate} setStartDate={setStartDate} planWeeks={draft.weeks.length} reviewed={reviewed} setReviewed={setReviewed} onAssign={approveAssign} assigning={assigning} onSaveTemplate={saveTemplate} savingTemplate={savingTemplate} color='#65A30D' />
          </div>
        )}
      </div>
    </div>
  );
}

const NUT_GOALS = [
  { value: 'profile', label: "Client's profile goal" },
  { value: 'Lose Fat', label: 'Lose Fat' },
  { value: 'Build Muscle', label: 'Build Muscle' },
  { value: 'Strength & Longevity', label: 'Strength & Longevity' },
];

function NutritionAI({ prefill }) {
  const clientId = prefill?.client_id || null;
  const [f, setF] = useState({ goal: '', calories: 1800, protein: 140, dietary: 'No Preference', structure: '3-if', weeks: 4, notes: '' });
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [startDate, setStartDate] = useState(localDate());
  const [reviewed, setReviewed] = useState(false);
  const [weekIdx, setWeekIdx] = useState(0);

  useEffect(() => { if (clientId) setF(prev => ({ ...prev, goal: 'profile' })); }, [clientId]);

  const generate = async () => {
    if (!clientId && !f.goal) return toast.error('Choose a goal: Lose Fat, Build Muscle or Strength & Longevity');
    setLoading(true); setReviewed(false); setWeekIdx(0); setProgram(null);
    try {
      const body = { goal: f.goal === 'profile' ? '' : f.goal, weeks: f.weeks, ...structureFields(f.structure),
        dietary: f.dietary || 'No Preference', notes: f.notes, client_id: clientId,
        calories: clientId ? 0 : f.calories, protein: clientId ? 0 : f.protein };
      const r = await api.post('/nutrition-programs/ai-generate', body);
      setProgram(r.data);
      toast[r.data.validation?.passed ? 'success' : 'warning'](r.data.validation?.passed
        ? 'Programme generated — all days within tolerance' : `Programme generated — ${r.data.validation?.failed_days} day(s) flagged`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    finally { setLoading(false); }
  };

  const assign = async () => {
    setAssigning(true);
    try {
      const r = await api.post('/nutrition-programs/assign', { client_id: clientId, program_id: program.id, start_date: startDate });
      toast.success(`Assigned · ${r.data.days_created} days scheduled`);
      setProgram(null); setReviewed(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Assign failed'); }
    finally { setAssigning(false); }
  };

  const passed = !!program?.validation?.passed;
  const ready = clientId && startDate && reviewed && passed;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="regen-card-elevated p-6 space-y-4">
        <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#FFB800]" /><div className="font-display font-bold">Nutrition Generator</div>
          {clientId && <span className="pill bg-[var(--c-green-soft)] text-[var(--c-green-ink)] text-[10px] ml-auto">From profile</span>}</div>
        <p className="text-xs text-muted-foreground">Selects approved meals from your Meal Library only. Portions are adjusted within limits and every macro is calculated from the Food Library.</p>
        <div><Label>Programme Goal</Label>
          <Select value={f.goal} onValueChange={v => setF({ ...f, goal: v })}>
            <SelectTrigger className="bg-regen-card border-[var(--c-hair)]" data-testid="ai-nut-goal"><SelectValue placeholder="Choose goal" /></SelectTrigger>
            <SelectContent>{NUT_GOALS.filter(g => clientId || g.value !== 'profile').map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {clientId ? (
          <div className="text-xs rounded-xl bg-white/[0.03] border border-[var(--c-hair)] p-3 text-muted-foreground" data-testid="ai-nut-targets-from-profile">
            Calorie and protein targets are calculated from {prefill.name}'s profile and the programme goal.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Daily kcal</Label><Input type="number" value={f.calories} onChange={e => setF({ ...f, calories: parseInt(e.target.value) || 0 })} data-testid="ai-nut-calories" className="bg-regen-card border-[var(--c-hair)] font-mono" /></div>
            <div><Label>Protein (g)</Label><Input type="number" value={f.protein} onChange={e => setF({ ...f, protein: parseInt(e.target.value) || 0 })} data-testid="ai-nut-protein" className="bg-regen-card border-[var(--c-hair)] font-mono" /></div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Meals / Day</Label><AdminMealStructure value={f.structure} onChange={v => setF({ ...f, structure: v })} fromClient={!!clientId} testid="ai-nut-meal-structure" /></div>
          <div><Label>Diet Preference</Label><Input value={f.dietary} onChange={e => setF({ ...f, dietary: e.target.value })} data-testid="ai-nut-dietary" className="bg-regen-card border-[var(--c-hair)]" /></div>
        </div>
        <DurationSelect value={f.weeks} onChange={v => setF({ ...f, weeks: v })} />
        <div><Label>Notes</Label><Textarea value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} placeholder="allergies, no eggs, no dairy…" data-testid="ai-nut-notes" className="bg-regen-card border-[var(--c-hair)]" /></div>
        <Button onClick={generate} disabled={loading} data-testid="ai-nut-generate" className="w-full bg-[#FFB800] text-[#081726] font-semibold">
          <Sparkles className="w-4 h-4 mr-2" /> {loading ? 'Generating…' : 'Generate Nutrition Programme'}
        </Button>
      </div>

      <div className="regen-card-elevated p-6">
        <div className="flex items-center gap-2 mb-4"><Eye className="w-5 h-5 text-[var(--c-green-ink)]" /><div className="font-display font-bold">Review Programme</div></div>
        {loading ? <GeneratingState elapsed={0} color="#FFB800" /> : !program ? <div className="text-muted-foreground text-sm py-12 text-center">Generate a programme to review.</div> : (
          <div className="space-y-3">
            <DraftBanner name={prefill?.name} />
            <div className="text-xs text-muted-foreground" data-testid="ai-nut-saved-note">Saved to Nutrition Programs as “{program.name}”. Edit meals there; days are re-validated on save.</div>
            <WeekTabs weeks={program.weeks} active={weekIdx} onSelect={setWeekIdx} showFocus={false} />
            <NutritionProgramReview program={program} weekIndex={weekIdx} />
            <div className="border-t border-[var(--c-hair)] mt-4 pt-4 space-y-3" data-testid="ai-nut-assign-controls">
              <div><Label>Start Date</Label><Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="ai-nut-start-date" className="bg-regen-card border-[var(--c-hair)] font-mono" /></div>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <Checkbox checked={reviewed} onCheckedChange={v => setReviewed(!!v)} data-testid="ai-nut-reviewed-check" /> I've reviewed this programme
              </label>
              {!clientId && <div className="text-xs text-[#FFB800]">Select a client above to assign this programme.</div>}
              {!passed && <div className="text-xs text-red-400" data-testid="ai-nut-assign-blocked">Assignment is blocked: some days are outside the calorie tolerance.</div>}
              <Button onClick={assign} disabled={!ready || assigning} data-testid="ai-nut-approve-assign" className="w-full font-semibold disabled:opacity-40" style={{ background: '#FFB800', color: '#081726' }}>
                <CheckCircle2 className="w-4 h-4 mr-2" /> {assigning ? 'Assigning…' : 'Approve & Assign'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
